<template>
    <div
        class="group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500  hover:-translate-y-2 overflow-hidden border border-gray-100"
        @mouseenter="showTooltip = true"
        @mouseleave="showTooltip = false"
    >
        <!-- Tooltip amélioré -->
        <div
            v-if="showTooltip"
            class="absolute z-50 -top-4 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-900/95 backdrop-blur-md text-white text-sm rounded-2xl py-4 px-6 max-w-sm opacity-0 animate-tooltip-in shadow-2xl border border-white/10"
            :class="{ 'opacity-100': showTooltip }"
        >
            <div class="font-bold mb-2 text-blue-200">{{ resource.title }}</div>
            <div class="text-gray-200 leading-relaxed mb-3">{{ resource.description }}</div>
            <div class="flex flex-wrap gap-2 mb-3">
        <span v-for="tag in resource.tags.slice(0, 4)" :key="tag"
              class="px-2 py-1 bg-white/20 rounded-lg text-xs font-medium">
          {{ tag }}
        </span>
            </div>
            <div class="grid grid-cols-2 gap-3 text-xs border-t border-white/20 pt-3">
                <div class="text-emerald-300">
                    <span class="font-medium">Popularité:</span> {{ resource.popularity }}%
                </div>
                <div class="text-cyan-300">
                    <span class="font-medium">Auteur:</span> {{ resource.author }}
                </div>
                <div class="text-amber-300">
                    <span class="font-medium">Date:</span> {{ formatDate(resource.date) }}
                </div>
                <div class="text-purple-300">
                    <span class="font-medium">{{ getDurationLabel(resource.type) }}:</span> {{ getDurationValue(resource) }}
                </div>
            </div>
            <!-- Flèche du tooltip -->
            <div class="absolute top-full left-1/2 transform -translate-x-1/2">
                <div class="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-transparent border-t-gray-900/95"></div>
            </div>
        </div>

        <!-- Image de couverture ou icône -->
        <div class="relative h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
            <img
                v-if="resource.cover"
                :src="resource.cover"
                :alt="resource.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                @error="handleImageError"
            >
            <div v-else class="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-purple-50">
                <div class="text-6xl opacity-80">{{ resource.image }}</div>
            </div>

            <!-- Overlay avec gradient -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <!-- Badge de type flottant -->
            <div class="absolute top-4 left-4">
        <span class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md border border-white/20 shadow-lg text-white bg-[#ff7e41]">
          {{ getTypeLabel(resource.type) }}
        </span>
            </div>
            <!-- Bouton play pour les vidéos/audio -->
            <div v-if="resource.type === 'video' || resource.type === 'audio'"
                 class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div class="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30  hover:scale-110 transition-transform duration-200">
                    <svg class="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                    </svg>
                </div>
            </div>
        </div>

        <!-- Contenu de la carte -->
        <div class="p-6">
            <!-- Titre -->
            <h3 class="text-xl font-bold text-gray-900 mb-3 line-clamp-2  transition-colors duration-300">
                {{ resource.title }}
            </h3>
            <!-- Auteur et meta info -->
            <div class="flex items-center text-sm text-gray-600 mb-3">
                <div class="flex items-center flex-1">
                    <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    <span class="font-medium">Author</span>
                </div>
                <div class="flex items-center text-xs text-gray-500">
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    18:30
                </div>
            </div>

            <!-- Description courte -->
            <p class="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                {{ truncateDescription(resource.description) }}
            </p>
            <div class="flex flex-wrap gap-1.5 mb-5">
        <span
            v-for="tag in resource.tags.slice(0, 3)"
            :key="tag"
            class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-[#2A3992] text-white  transition-all duration-200"
        >
          {{ tag }}
        </span>
                <span
                    v-if="resource.tags.length > 3"
                    class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200"
                >
          +{{ resource.tags.length - 3 }}
        </span>
            </div>

            <div class="flex items-center justify-between">
                <div class="text-sm text-gray-500">
                    <div class="flex items-center">
                        <svg class="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        {{ formatDate(resource.date) }}
                    </div>
                </div>
                <button
                    class="inline-flex items-center px-4 py-2.5  border border-[#2A3992] text-[#2A3992] hover:bg-[#2A3992] rounded-xl text-sm font-bold hover:text-white transition-all duration-300  hover:scale-105 shadow-lg hover:shadow-xl"
                >
                    Voir plus
                    <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Effet de brillance au survol -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none"></div>
    </div>
</template>

<script setup>
import { ref } from 'vue'

// Props
const props = defineProps({
    resource: {
        type: Object,
        required: true
    }
})

// État local
const showTooltip = ref(false)

// Méthodes utilitaires améliorées
const getTypeStyle = (type) => {
    const styles = {
        video: 'bg-red-500/90 text-white',
        audio: 'bg-purple-500/90 text-white',
        article: 'bg-blue-500/90 text-white',
        pdf: 'bg-orange-500/90 text-white',
        event: 'bg-emerald-500/90 text-white'
    }
    return styles[type] || 'bg-gray-500/90 text-white'
}


const getTypeLabel = (type) => {
    const labels = {
        video: 'Vidéo',
        audio: 'Podcast',
        article: 'Article',
        pdf: 'Document',
        event: 'Événement'
    }
    return labels[type] || 'Ressource'
}



const getDurationLabel = (type) => {
    const labels = {
        video: 'Durée',
        audio: 'Durée',
        article: 'Lecture',
        pdf: 'Pages',
        event: 'Durée'
    }
    return labels[type] || 'Info'
}

const getDurationValue = (resource) => {
    return resource.duration || resource.readTime || resource.pages || 'N/A'
}

const truncateDescription = (description) => {
    return description.length > 100 ? description.substring(0, 100) + '...' : description
}

const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    })
}

const handleImageError = (event) => {
    event.target.style.display = 'none'
    // Vous pourriez ici déclencher l'affichage d'une image par défaut
}

const handleAction = (resource) => {
    console.log(`Action pour la ressource: ${resource.title}`)
    // Animation de feedback
    event.target.classList.add('animate-pulse')
    setTimeout(() => {
        event.target.classList.remove('animate-pulse')
    }, 200)

    // Logique spécifique selon le type
    switch (resource.type) {
        case 'video':
        case 'audio':
            // Rediriger vers le lecteur
            break
        case 'article':
            // Ouvrir l'article
            break
        case 'pdf':
            // Télécharger le PDF
            break
        case 'event':
            // Ouvrir la page d'inscription
            break
        default:
            break
    }
}
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.animate-tooltip-in {
    animation: tooltipIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes tooltipIn {
    from {
        opacity: 0;
        transform: translate(-50%, -100%) scale(0.95) translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -100%) scale(1) translateY(0);
    }
}

/* Animation personnalisée pour les cartes */
@keyframes shimmer {
    0% {
        transform: translateX(-100%) skewX(-12deg);
    }
    100% {
        transform: translateX(200%) skewX(-12deg);
    }
}

.group:hover .animate-shimmer {
    animation: shimmer 1s ease-in-out;
}
</style>
